import React from 'react';
import {
  Box,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Image,
  Badge,
  Grid,
  useColorModeValue,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import {
  FiEye,
  FiThumbsUp,
  FiShare2,
  FiDollarSign,
  FiClock,
  FiMoreVertical,
  FiEdit,
  FiTrash2,
  FiShare
} from 'react-icons/fi';
// ContentPerformance interface is defined locally below
import CachedImage from './CachedImage';
import IconWrapper from './IconWrapper';

interface Performance {
  id: string;
  title: string;
  thumbnail: string;
  duration: string;
  views: number;
  likes: number;
  shares: number;
  donations: number;
  uploadDate: Date;
  category?: string;
}

interface ContentPerformanceProps {
  content: Performance;
  onViewDetails?: (contentId: string) => void;
  onEdit?: (contentId: string) => void;
  onDelete?: (contentId: string) => void;
  onShare?: (contentId: string) => void;
}

const ContentPerformance: React.FC<ContentPerformanceProps> = ({ 
  content, 
  onViewDetails,
  onEdit,
  onDelete,
  onShare
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  return (
    <Card 
      bg={cardBg} 
      shadow="sm" 
      _hover={{ shadow: 'md' }} 
      transition="all 0.2s"
      cursor="pointer"
      onClick={() => onViewDetails?.(content.id)}
    >
      <CardBody p={{ base: 3, md: 4 }}>
        <HStack align="start" gap={{ base: 3, md: 4 }}>
          {/* Thumbnail */}
          <Box position="relative" flexShrink={0}>
            {content.thumbnail ? (
              <CachedImage
                src={content.thumbnail}
                alt={content.title}
                width="120px"
                height="68px"
                borderRadius="md"
                objectFit="cover"
                fallbackType="video"
              />
            ) : (
              <Box
                w={{ base: "80px", md: "120px" }}
                h={{ base: "45px", md: "68px" }}
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconWrapper icon={FiEye} size={24} color="gray.400" />
              </Box>
            )}
            
            {/* Play button overlay */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              w={{ base: 6, md: 8 }}
              h={{ base: 6, md: 8 }}
              bg="rgba(0, 0, 0, 0.7)"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="white"
            >
              <IconWrapper icon={FiEye} size={16} />
            </Box>
          </Box>

          {/* Content Details */}
          <VStack align="start" flex="1" gap={{ base: 1, md: 2 }}>
            <HStack justify="space-between" width="100%">
              <VStack align="start" gap={1} flex="1">
                <HStack>
                  <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="bold" noOfLines={2}>
                    {content.title}
                  </Text>
                  {content.category && (
                    <Badge colorScheme="purple" variant="subtle" fontSize="0.6rem">
                      {content.category}
                    </Badge>
                  )}
                </HStack>
                <HStack gap={2}>
                  {/* Only show duration for non-book content */}
                  {content.category !== 'book' && (
                    <>
                      <HStack gap={1}>
                        <IconWrapper icon={FiClock} size={12} color={textColor} />
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor}>
                          {content.duration}
                        </Text>
                      </HStack>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor}>
                        •
                      </Text>
                    </>
                  )}
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor}>
                    {formatDate(content.uploadDate)}
                  </Text>
                </HStack>
              </VStack>
              
              {/* Action Menu */}
              {(onEdit || onDelete || onShare) && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<IconWrapper icon={FiMoreVertical} size={18} />}
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MenuList onClick={(e) => e.stopPropagation()}>
                    {onEdit && (
                      <MenuItem icon={<IconWrapper icon={FiEdit} size={16} />} onClick={() => onEdit(content.id)}>
                        Edit
                      </MenuItem>
                    )}
                    {onShare && (
                      <MenuItem icon={<IconWrapper icon={FiShare} size={16} />} onClick={() => onShare(content.id)}>
                        Share
                      </MenuItem>
                    )}
                    {onDelete && (
                      <MenuItem 
                        icon={<IconWrapper icon={FiTrash2} size={16} />} 
                        onClick={() => onDelete(content.id)}
                        color="red.500"
                      >
                        Delete
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              )}
            </HStack>

            {/* Performance Metrics */}
            <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={{ base: 2, md: 4 }} width="100%">
              <VStack gap={1}>
                <HStack gap={1}>
                  <IconWrapper icon={FiEye} size={16} color="blue.500" />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">Views</Text>
                </HStack>
                <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="bold" color="blue.500">
                  {content.views.toLocaleString()}
                </Text>
              </VStack>

              <VStack gap={1}>
                <HStack gap={1}>
                  <IconWrapper icon={FiThumbsUp} size={16} color="red.500" />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">Likes</Text>
                </HStack>
                <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="bold" color="red.500">
                  {content.likes.toLocaleString()}
                </Text>
              </VStack>

              <VStack gap={1}>
                <HStack gap={1}>
                  <IconWrapper icon={FiShare2} size={16} color="purple.500" />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">Shares</Text>
                </HStack>
                <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="bold" color="purple.500">
                  {content.shares.toLocaleString()}
                </Text>
              </VStack>

              <VStack gap={1}>
                <HStack gap={1}>
                  <IconWrapper icon={FiDollarSign} size={16} color="yellow.500" />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">Donation</Text>
                </HStack>
                <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="bold" color="yellow.500">
                  ${content.donations.toLocaleString()}
                </Text>
              </VStack>
            </Grid>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default ContentPerformance;
